import type {
  EnvironmentId,
  MessageId,
  ModelSelection,
  OrchestrationLatestTurn,
  ProjectId,
  ProviderInteractionMode,
  RuntimeMode,
  ThreadId,
  TurnId,
} from "@cleo/contracts";
import type { CreateThreadInput } from "@cleo/client-runtime/operations";

/**
 * Fork from here (Can 46, increment I3): a Local conversation's message
 * starts a new conversation that carries everything said up to and including
 * it. The server (`thread.create` with `forkedFrom`) copies the history and
 * refuses what it cannot fork; this module builds the one command, names the
 * messages the server would refuse, and runs the create-then-open sequence.
 */

/** The source conversation, as the Desk holds it. */
export interface ForkSource {
  readonly id: ThreadId;
  readonly environmentId: EnvironmentId;
  readonly projectId: ProjectId;
  readonly title: string;
  readonly branch: string | null;
  readonly worktreePath: string | null;
  readonly modelSelection: ModelSelection;
  readonly runtimeMode: RuntimeMode;
  readonly interactionMode: ProviderInteractionMode;
}

/**
 * The source "Fork from here" forks from as a Local fork: a Local
 * conversation the server holds. A World conversation forks through its
 * Session (`worldForkSource`), and a draft has no messages to fork from.
 */
export function localForkSource<
  T extends ForkSource & { readonly worldKey?: string | null | undefined },
>(thread: T | null | undefined, isServerThread: boolean): T | null {
  if (!isServerThread || !thread) return null;
  return thread.worldKey == null ? thread : null;
}

/**
 * A World conversation forks through its Session (package I4): a sibling
 * Session in the same room of the same World, whose execution is created
 * with `forkedFrom`. The coordinates come from the source itself, so the
 * fork can never land in another World, and a Local conversation never takes
 * this route (nor a World one the Local route). A World execution without a
 * Session, or one already retired, offers no fork.
 */
export function worldForkSource<
  T extends {
    readonly worldKey?: string | null | undefined;
    readonly sessionId?: string | null | undefined;
    readonly archivedAt: string | null;
  },
>(
  thread: T | null | undefined,
  isServerThread: boolean,
): { readonly worldKey: string; readonly sessionId: string; readonly source: T } | null {
  if (!isServerThread || !thread) return null;
  const worldKey = thread.worldKey?.trim() ?? "";
  const sessionId = thread.sessionId?.trim() ?? "";
  if (worldKey === "" || sessionId === "" || thread.archivedAt !== null) return null;
  return { worldKey, sessionId, source: thread };
}

export type SessionForkPoint =
  | { readonly ok: true; readonly messageId: MessageId | null }
  | { readonly ok: false; readonly reason: string };

/**
 * "Fork session" forks at the latest message: the reply of the latest
 * finished turn, or its person's message when there was no reply. A
 * conversation with no turn yet has nothing to carry (`messageId` null). A
 * running turn is refused with the same sentence "Fork from here" shows.
 */
export function sessionForkPoint(source: {
  readonly latestTurn: Pick<
    OrchestrationLatestTurn,
    "state" | "completedAt" | "assistantMessageId" | "userMessageId"
  > | null;
}): SessionForkPoint {
  const latest = source.latestTurn;
  if (latest === null) return { ok: true, messageId: null };
  if (latest.state === "running" || latest.completedAt === null)
    return { ok: false, reason: FORK_BLOCKED_RUNNING };
  const messageId = latest.assistantMessageId ?? latest.userMessageId ?? null;
  return messageId === null
    ? {
        ok: false,
        reason: "Its latest message is not addressable. Use Fork from here on a message.",
      }
    : { ok: true, messageId };
}

export function forkTitle(sourceTitle: string): string {
  const title = sourceTitle.trim();
  return title.length === 0 ? "Fork" : `Fork of «${title}»`;
}

/**
 * The exact `thread.create` a fork dispatches. The fork starts in the same
 * project, branch and worktree with the source's current engine selection;
 * the person may choose another engine before the fork's first Send, because
 * inherited entries are not the fork's own messages and the engine lock
 * engages only once a thread has one.
 */
export function prepareForkThreadCommand(input: {
  readonly source: ForkSource;
  readonly throughMessageId: MessageId;
  readonly threadId: ThreadId;
}): CreateThreadInput {
  const { source } = input;
  return {
    threadId: input.threadId,
    projectId: source.projectId,
    title: forkTitle(source.title),
    forkedFrom: { threadId: source.id, throughMessageId: input.throughMessageId },
    modelSelection: source.modelSelection,
    runtimeMode: source.runtimeMode,
    interactionMode: source.interactionMode,
    branch: source.branch,
    worktreePath: source.worktreePath,
  };
}

export const FORK_BLOCKED_STREAMING =
  "This reply is still being written. Fork from it once it is finished.";
export const FORK_BLOCKED_RUNNING =
  "This message belongs to a reply that is still running. Fork from it once it is finished.";

interface ForkableMessage {
  readonly id: MessageId;
  readonly role: "user" | "assistant" | "system";
  readonly turnId?: TurnId | null | undefined;
  readonly streaming: boolean;
  readonly createdAt: string;
}

/**
 * The messages "Fork from here" is disabled on, with the server's own
 * sentence: a reply still being written, and any message of a turn that is
 * pending or running. The server decides; this only keeps the person from
 * choosing what it would refuse.
 */
export function deriveForkBlockedMessages(input: {
  readonly messages: ReadonlyArray<ForkableMessage>;
  readonly latestTurn:
    | (Pick<
        OrchestrationLatestTurn,
        "turnId" | "state" | "requestedAt" | "completedAt" | "assistantMessageId"
      > & { readonly userMessageId?: MessageId | null | undefined })
    | null;
  readonly runningTurnId: TurnId | null;
  /** A Send is in flight or the thread's session reports work. */
  readonly working: boolean;
}): ReadonlyMap<MessageId, string> {
  const blocked = new Map<MessageId, string>();
  const latest = input.latestTurn;
  const latestRunning =
    latest !== null && (latest.state === "running" || latest.completedAt === null);
  const busyTurnIds = new Set<TurnId>();
  if (input.runningTurnId !== null) busyTurnIds.add(input.runningTurnId);
  if (latest !== null && latestRunning) busyTurnIds.add(latest.turnId);
  for (const message of input.messages) {
    if (message.streaming) {
      blocked.set(message.id, FORK_BLOCKED_STREAMING);
      continue;
    }
    const inBusyTurn =
      (message.turnId != null && busyTurnIds.has(message.turnId)) ||
      (latest !== null &&
        latestRunning &&
        (latest.userMessageId === message.id || latest.assistantMessageId === message.id));
    if (inBusyTurn) blocked.set(message.id, FORK_BLOCKED_RUNNING);
  }
  // A Send whose turn has not been recorded yet: the person's newest message
  // with no turn of its own is the turn being started.
  if (input.working || latestRunning) {
    const lastUser = input.messages.findLast((message) => message.role === "user");
    if (
      lastUser !== undefined &&
      lastUser.turnId == null &&
      (latest === null || lastUser.createdAt >= latest.requestedAt)
    )
      blocked.set(lastUser.id, FORK_BLOCKED_RUNNING);
  }
  return blocked;
}

const INVARIANT_PREFIX = /^Orchestration command invariant failed \([^)]*\):\s*/u;

/** The server's own sentence for a refused fork, without the engine's wrapper. */
export function forkRefusalSentence(error: unknown): string {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "object" &&
          error !== null &&
          typeof (error as { message?: unknown }).message === "string"
        ? (error as { message: string }).message
        : "";
  const sentence = message.replace(INVARIANT_PREFIX, "").trim();
  return sentence.length > 0 ? sentence : "The conversation could not be forked.";
}

export type ForkDispatchResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly error: unknown };

export interface ForkFromHereDeps {
  readonly newThreadId: () => ThreadId;
  readonly dispatch: (
    environmentId: EnvironmentId,
    input: CreateThreadInput,
  ) => Promise<ForkDispatchResult>;
  /** Resolves true once the created thread has reached this Desk. */
  readonly waitForThread: (environmentId: EnvironmentId, threadId: ThreadId) => Promise<boolean>;
  readonly openThread: (environmentId: EnvironmentId, threadId: ThreadId) => Promise<void>;
  readonly notify: (notice: {
    readonly type: "error" | "info";
    readonly title: string;
    readonly description: string;
  }) => void;
}

export type ForkFromHereOutcome =
  | { readonly _tag: "Opened"; readonly threadId: ThreadId }
  | { readonly _tag: "Pending"; readonly threadId: ThreadId }
  | { readonly _tag: "Refused"; readonly reason: string };

/**
 * Create the fork, then open it once its record has reached this Desk. A
 * failed acknowledgement is checked against the stream first: the record may
 * have been made although the answer was lost.
 */
export async function runForkFromHere(
  deps: ForkFromHereDeps,
  input: { readonly source: ForkSource; readonly throughMessageId: MessageId },
): Promise<ForkFromHereOutcome> {
  const threadId = deps.newThreadId();
  const command = prepareForkThreadCommand({ ...input, threadId });
  const environmentId = input.source.environmentId;
  let dispatched: ForkDispatchResult;
  try {
    dispatched = await deps.dispatch(environmentId, command);
  } catch (error) {
    dispatched = { ok: false, error };
  }
  if (!dispatched.ok && isDefiniteRefusal(dispatched.error)) {
    const reason = forkRefusalSentence(dispatched.error);
    deps.notify({
      type: "error",
      title: "This conversation could not be forked",
      description: reason,
    });
    return { _tag: "Refused", reason };
  }
  const arrived = await deps.waitForThread(environmentId, threadId).catch(() => false);
  if (arrived) {
    await deps.openThread(environmentId, threadId);
    return { _tag: "Opened", threadId };
  }
  if (!dispatched.ok) {
    const reason = forkRefusalSentence(dispatched.error);
    deps.notify({
      type: "error",
      title: "This conversation could not be forked",
      description: reason,
    });
    return { _tag: "Refused", reason };
  }
  deps.notify({
    type: "info",
    title: "The fork was made",
    description: "It opens in the sidebar when this Desk hears of it.",
  });
  return { _tag: "Pending", threadId };
}

/**
 * A decider refusal records nothing, so there is no thread to wait for. The
 * server marks a rejected Send explicitly and reports a decider refusal of any
 * other command with the engine's invariant wrapper around its sentence.
 */
function isDefiniteRefusal(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  if ((error as { commandOutcome?: unknown }).commandOutcome === "rejected") return true;
  const message = (error as { message?: unknown }).message;
  return typeof message === "string" && INVARIANT_PREFIX.test(message);
}
